import React from 'react';
import { Calendar, Clock, MapPin, Award, Star, ExternalLink, Coffee } from 'lucide-react';
import ProgramSchedule from '@/components/ProgramSchedule';

const ATTRACTIONS = [
  {
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_agos2026/artifacts/jvmi875z_IMG_1451.png',
    title: 'Teachers of Tomorrow',
    subtitle: 'A Platform to Inspire a Community to Grow',
    date: '24–25 October 2026',
    venue: 'Le Méridien, Surat',
    details: [
      'Team Structure: 1 AGOS Teacher (Mentor) + 2 Postgraduates/SRs/Fellows (Mentees)',
      'Role of Mentor: Guide the team & handle all communications with the Scientific Committee',
      'Topic & Timing: 2 random topics assigned 1–2 weeks before event. Each team gets 3:30 min Presentation + 2 min Discussion',
      'Evaluation: Live judging of teaching skills immediately after presentation',
    ],
    awards: 'Awards for Top 3 Teams, Best Mentor, Best Mentee',
    directors: ['Dr. Parth Rana', 'Dr. Arvind Morya', 'Dr. Ashish Bhojak'],
    directorsLabel: 'Program Directors',
    deadline: 'Submit entries before: 12 Sep 2026',
  },
  {
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_agos2026/artifacts/995m8515_IMG_1452.png',
    title: 'Pre-Conference Workshop',
    subtitle: 'Basics in Ophthalmology – Mastering the Fundamentals',
    date: '23rd October 2026',
    time: '2:00 PM – 6:00 PM',
    venue: 'With Lunch & High Tea',
    fee: '₹1,000/-',
    details: [
      'Anterior Segment sessions',
      'Posterior Segment sessions (includes Basic & Detailed)',
    ],
  },
  {
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_agos2026/artifacts/7ex078bx_PHOTOGRAPHY%20COMPETITION%20%282%29.png',
    title: 'Ocular Lens Photography Competition',
    subtitle: 'Capture the Unseen Details of Vision and Life',
    date: '24th – 25th October 2026',
    venue: 'Le Méridien, Surat, Gujarat',
    details: [
      'Categories – Non Ophthalmic: Landscape, Candid',
      'Categories – Ophthalmic: Anterior Segment, Posterior Segment',
      'Limit: 1 Entry / Person / Category',
      'Format: High-Res JPEG (300 DPI | 3 MB – 10 MB)',
      'Include: Title, Full Name, AGOC Reg. ID & Category',
      'Submit via WeTransfer to sgaosurat2025@gmail.com',
    ],
    deadline: 'Last date of submission: 30 September 2026',
  },
  {
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_agos2026/artifacts/f6wkipwd_WETLAB%20%28SSTC%29%20%281%29.png',
    title: 'Wetlab (SSTC) Surgical Skill Transfer Course',
    subtitle: 'Hands-on Surgical Training',
    date: '23rd October 2026',
    time: '10:00 AM – 6:00 PM (With Lunch & High Tea)',
    venue: 'Juno Moneta, Near Raj Hans Multiplex, Pal Hazira Road',
    fee: 'FREE (Registration Compulsory)',
    details: [
      'PHACO',
      'INTRAVITREAL',
      'ICL',
      'ROP',
      'GLAUCOMA',
      'SQUINT',
      'CORNEAL GRAFT AND SUTURING',
    ],
    contact: 'Register: sgaosurat2025@gmail.com',
    link: 'https://docs.google.com/forms/d/e/1FAIpQLSduuMxfHaL_87QBeFVzx-VZGjH5JUBM6VYDyzrB3qihd8Hi0Q/viewform?usp=header',
    linkText: 'Register for Wetlab (SSTC)',
  },
  {
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_agos2026/artifacts/8jlss1xm__Raghudeep%20Film%20Festival%20%281%29.png',
    title: 'Raghudeep Film Festival',
    subtitle: 'The AGOC 2026 Video Showcase',
    details: [
      'Step 1: Submit your video abstract via the Google Form',
      'Step 2: Bring your final audio-visual file (video/film) on a pen drive on-site',
    ],
    deadline: 'Last date to submit: 15th September 2026',
    link: 'https://forms.gle/C2VGUDwpPmzcvBR28',
    linkText: 'Submit via Google Form',
  },
];

const PRE_CONF_HALLS = [
  {
    hall: 'Hall A – Anterior Segment & Adnexa',
    accent: '#FF6F4A',
    rows: [
      { time: '2.00 – 2.30 pm', topic: 'Pentacam interpretation', speaker: 'Dr. Pratik Topiwala' },
      { time: '2.30 – 2.50 pm', topic: 'Keratoconus Progression Assessment on Pentacam', speaker: 'Dr. Pratik Topiwala' },
      { time: '2.50 – 3.10 pm', topic: "BCL Do's & Don'ts", speaker: 'Dr. Sankit Shah' },
      { time: '3.10 – 3.35 pm', topic: 'Basic Management of Corneal Ulcer', speaker: 'Dr. Shobita Shah' },
      { time: '3.35 – 4.00 pm', topic: 'Dry eye Assessment', speaker: 'Dr. Sankit Shah' },
      { time: '4.00 – 4.30 pm', topic: 'High Tea', speaker: '', isBreak: true },
      { time: '4.30 – 4.50 pm', topic: 'Ptosis Assessment', speaker: 'Dr. Saurin Gandhi' },
      { time: '4.50 – 5.05 pm', topic: "Botox Do's & Don'ts", speaker: 'Dr. Priyata Sheth' },
      { time: '5.05 – 5.35 pm', topic: "Optical Biometry Do's & Don'ts", speaker: 'Dr. Nitin M. Patel' },
      { time: '5.35 – 6.00 pm', topic: 'Pupil & Premium IOLs', speaker: 'Dr. Dishant Shah' },
    ],
  },
  {
    hall: 'Hall B – Posterior Segment & Glaucoma',
    accent: '#FFB6C1',
    rows: [
      { time: '2.00 – 2.25 pm', topic: 'Macula OCT interpretation', speaker: 'Dr. Saurin Shroff' },
      { time: '2.25 – 2.50 pm', topic: 'Diabetic Retinopathy Mx Protocol', speaker: 'Dr. Shobhna Mange' },
      { time: '2.50 – 3.15 pm', topic: 'Endophthalmitis Basic Mx & Intravitreal Injections', speaker: 'Dr. Pathik Barwalia' },
      { time: '3.15 – 3.40 pm', topic: 'ROP Screening', speaker: 'Dr. Vaibhavi Noticewala' },
      { time: '3.40 – 4.05 pm', topic: 'B-Scan', speaker: 'Dr. Vinit Shah' },
      { time: '4.05 – 4.30 pm', topic: 'High Tea', speaker: '', isBreak: true },
      { time: '4.30 – 4.45 pm', topic: "Neuroimaging Do's & Don'ts", speaker: 'Dr. Vimal Fudnawala' },
      { time: '4.45 – 5.05 pm', topic: 'Glaucoma OCT interpretation', speaker: 'Dr. Manan Jariwala' },
      { time: '5.05 – 5.30 pm', topic: 'Perimetry Interpretation', speaker: 'Dr. Kunj Naik' },
      { time: '5.30 – 6.00 pm', topic: "Medical Mx of Glaucoma Do's & Don'ts", speaker: 'Dr. Kaivan Shah' },
    ],
  },
];

const ScientificProgramPage = () => {
  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="scientific-program-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="program-title">
            Scientific <span className="gradient-text">Program</span>
          </h1>
          <p className="text-lg text-slate-300">Two days of comprehensive ophthalmology education</p>
        </div>

        {/* Full Scientific Programme */}
        <ProgramSchedule />

        {/* Pre-Conference Workshop Schedule */}
        <div className="mb-12" data-testid="pre-conference-schedule">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">
              Pre-Conference <span className="gradient-text">Workshop Schedule</span>
            </h2>
            <p className="text-slate-300 flex items-center justify-center gap-2 flex-wrap">
              <Calendar className="h-4 w-4 text-[#FF6F4A]" /> 23 October 2026 (Friday)
              <span className="text-slate-500">•</span>
              <Clock className="h-4 w-4 text-[#FF6F4A]" /> Lunch 1:00 – 2:00 PM, followed by sessions
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {PRE_CONF_HALLS.map((hall, hIdx) => (
              <div key={hIdx} className="glass-card overflow-hidden" data-testid={`pre-conf-hall-${hIdx}`}>
                <div className="px-6 py-4 border-b border-white/10" style={{ background: `${hall.accent}18` }}>
                  <h3 className="text-lg font-bold text-white">{hall.hall}</h3>
                </div>
                <div className="divide-y divide-white/5">
                  {hall.rows.map((row, rIdx) => (
                    <div
                      key={rIdx}
                      className={`flex items-start gap-4 px-6 py-3 ${row.isBreak ? 'bg-white/5' : 'hover:bg-white/5'} transition-colors`}
                    >
                      <span className="text-xs font-mono text-slate-400 whitespace-nowrap pt-0.5 min-w-[92px]">{row.time}</span>
                      {row.isBreak ? (
                        <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: hall.accent }}>
                          <Coffee className="h-4 w-4" /> {row.topic}
                        </span>
                      ) : (
                        <div className="flex-1">
                          <p className="text-sm text-white leading-snug">{row.topic}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{row.speaker}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Attractions Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">
            Conference <span className="gradient-text">Attractions</span>
          </h2>
          <div className="space-y-10">
            {ATTRACTIONS.map((item, idx) => (
              <div key={idx} className="glass-card overflow-hidden" data-testid={`attraction-${idx}`}>
                <div className="md:flex">
                  <div className="md:w-1/2">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="md:w-1/2 p-6 md:p-8">
                    <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-slate-400 text-sm mb-4">{item.subtitle}</p>
                    <div className="space-y-2 mb-4">
                      {item.date && <p className="flex items-center gap-2 text-sm text-slate-300"><Calendar className="h-4 w-4 text-[#FF6F4A] flex-shrink-0" /> {item.date}</p>}
                      {item.time && <p className="flex items-center gap-2 text-sm text-slate-300"><Clock className="h-4 w-4 text-[#FF6F4A] flex-shrink-0" /> {item.time}</p>}
                      {item.venue && <p className="flex items-center gap-2 text-sm text-slate-300"><MapPin className="h-4 w-4 text-[#FF6F4A] flex-shrink-0" /> {item.venue}</p>}
                      {item.fee && <p className="text-sm font-semibold text-[#FF6F4A]">Fee: {item.fee}</p>}
                    </div>
                    {item.directors && (
                      <div className="mb-4">
                        <p className="text-xs text-slate-400 font-semibold mb-1">{item.directorsLabel}:</p>
                        <div className="flex flex-wrap gap-2">
                          {item.directors.map((d, i) => (
                            <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#FF6F4A]/20 text-[#FF6F4A]">{d}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <ul className="space-y-1.5 mb-3">
                      {item.details.map((d, i) => (
                        <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                          <Star className="h-3 w-3 text-[#FFB6C1] mt-0.5 flex-shrink-0" /> {d}
                        </li>
                      ))}
                    </ul>
                    {item.contact && <p className="text-xs text-slate-300 mb-2">{item.contact}</p>}
                    {item.awards && <p className="text-xs text-yellow-400 font-semibold flex items-center gap-1"><Award className="h-3 w-3" /> {item.awards}</p>}
                    {item.deadline && <p className="text-xs text-red-400 mt-1 font-semibold">{item.deadline}</p>}
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-4 bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors text-sm"
                        data-testid={`attraction-link-${idx}`}
                      >
                        <ExternalLink className="h-4 w-4" /> {item.linkText}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScientificProgramPage;
